import { type BaseSyntheticEvent, useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { StreamChat } from 'stream-chat';
import {
  Chat,
  Channel,
  ChannelHeader,
  ChannelList,
  MessageInput,
  MessageList,
  Window,
} from 'stream-chat-react';
import { Loader2 } from 'lucide-react';
import { useAuth } from '@/hooks/useAuth';
import { streamChatService } from '@/lib/api/streamChat.service';
import { CustomMessageInput } from '@/components/chat/CustomMessageInput';

export function StreamChatPage() {
  const { user } = useAuth();
  const [searchParams] = useSearchParams();
  const { t } = useTranslation(['common']);
  const [client, setClient] = useState<StreamChat | null>(null);
  const [streamUserId, setStreamUserId] = useState<string>('');
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');

  const channelCid = useMemo(() => searchParams.get('channel'), [searchParams]);
  const customActiveChannel = useMemo(() => {
    if (!channelCid) return undefined;
    const [, channelId] = channelCid.split(':');
    return channelId || undefined;
  }, [channelCid]);

  useEffect(() => {
    if (!user?.id) {
      setIsLoading(false);
      return;
    }

    let chatClient: StreamChat | null = null;

    const initChat = async () => {
      try {
        setIsLoading(true);
        setError('');

        const tokenData = await streamChatService.getUserToken();
        chatClient = StreamChat.getInstance(tokenData.apiKey);

        await chatClient.connectUser(
          {
            id: tokenData.userId,
            name: user.full_name,
            image: (user as { profile_image_url?: string }).profile_image_url,
          },
          tokenData.token,
        );

        setStreamUserId(tokenData.userId);
        setClient(chatClient);
      } catch (err) {
        setError(err instanceof Error ? err.message : t('common:errors.loadChat'));
      } finally {
        setIsLoading(false);
      }
    };

    initChat();

    return () => {
      if (chatClient) {
        chatClient.disconnectUser();
      }
    };
  }, [user]);

  const activeStreamUserId = streamUserId || String(user?.id ?? '');

  const handleChannelSearch = useCallback(
    async ({ setQuery, setResults, setSearching }: { setQuery: (value: string) => void; setResults: (value: any[]) => void; setSearching: (value: boolean) => void }, event: BaseSyntheticEvent) => {
      const rawValue = String(event?.target?.value ?? '');
      const normalizedQuery = rawValue.replace(/^[@#\s]+/, '').trim();

      setQuery(normalizedQuery);

      if (!normalizedQuery || !client || !activeStreamUserId) {
        setResults([]);
        setSearching(false);
        return;
      }

      setSearching(true);

      try {
        const [channels, usersResponse] = await Promise.all([
          client.queryChannels(
            {
              type: { $in: ['messaging', 'team'] },
              members: { $in: [activeStreamUserId] },
              name: { $autocomplete: normalizedQuery },
            } as any,
            { last_message_at: -1 },
            { limit: 10 },
          ),
          client.queryUsers(
            {
              id: { $ne: activeStreamUserId },
              $or: [{ name: { $autocomplete: normalizedQuery } }, { id: { $autocomplete: normalizedQuery } }],
            } as any,
            { id: 1 },
            { limit: 10 },
          ),
        ]);

        setResults([...(channels as any[]), ...((usersResponse as any)?.users ?? [])]);
      } catch {
        setResults([]);
      } finally {
        setSearching(false);
      }
    },
    [activeStreamUserId, client],
  );

  if (isLoading) {
    return (
      <div className="flex h-full items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin" />
      </div>
    );
  }

  if (error) {
    return <div className="flex h-full items-center justify-center text-sm text-red-600">{error}</div>;
  }

  if (!user || !client) {
    return (
      <div className="flex h-full items-center justify-center text-sm text-muted-foreground">
        {t('common:errors.loginRequired')}
      </div>
    );
  }

  return (
    <div className="stream-chat h-full w-full">
      <Chat client={client} theme="str-chat__theme-light">
        <div className="flex h-full min-h-0 w-full">
          <div className="h-full w-full border-r md:w-[360px] md:flex-shrink-0">
            <ChannelList
              filters={{ type: { $in: ['messaging', 'team'] }, members: { $in: [activeStreamUserId] } } as any}
              sort={{ last_message_at: -1 }}
              options={{ limit: 30, state: true, watch: true }}
              customActiveChannel={customActiveChannel}
              setActiveChannelOnMount
              showChannelSearch
              additionalChannelSearchProps={{
                searchForChannels: true,
                searchForUsers: true,
                searchDebounceIntervalMs: 250,
                searchFunction: handleChannelSearch,
              }}
            />
          </div>

          <div className="flex min-h-0 min-w-0 flex-1">
            <Channel Input={CustomMessageInput}>
              <Window>
                <ChannelHeader />
                <MessageList />
                <MessageInput />
              </Window>
            </Channel>
          </div>
        </div>
      </Chat>
    </div>
  );
}
